import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPrompt,
  deletePrompt,
  fetchIdeas,
  fetchInnovationItems,
  fetchPrompts,
  fetchSessionMessages,
  fetchSessions,
  fetchTopics,
  fetchUserLib,
  fmtRelative,
  sendChat,
} from "../lib/api";
import { toast } from "sonner";
import {
  ArrowDown,
  Bot,
  BookmarkPlus,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Eraser,
  Library,
  Mail,
  PlusCircle,
  RotateCcw,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { api } from "../lib/api";
import RedDisclaimer from "../components/RedDisclaimer";
import AIDisclaimer from "../components/AIDisclaimer";
import SummaryDiagramResults from "../components/SummaryDiagramResults";
import CategoryFilter from "../components/CategoryFilter";
import WorkspacePreview from "../components/WorkspacePreview";
import { EXAMPLE_SESSIONS } from "../data/exampleBotSessions";

const DISMISSED_EXAMPLES_KEY = "mindstein_dismissed_examples";

function loadDismissedExamples() {
  try {
    const raw = localStorage.getItem(DISMISSED_EXAMPLES_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function persistDismissedExamples(set) {
  try {
    localStorage.setItem(DISMISSED_EXAMPLES_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

const MODES = [
  { v: "combine", l: "COMBINE", hint: "Mash references into one new concept." },
  { v: "compare", l: "COMPARE", hint: "Side-by-side compare references." },
  { v: "expand", l: "EXPAND", hint: "Break one idea into sub-ideas." },
  { v: "rename", l: "RENAME", hint: "Punchier titles for an idea." },
  { v: "prioritize", l: "PRIORITIZE", hint: "Score & rank references." },
  { v: "validate", l: "VALIDATE", hint: "Check evidence using sources." },
  { v: "daily_synthesis", l: "DAILY_SYNTH", hint: "5-phase grounded pipeline: dual ranking plaques + multi-tier synthesis." },
  { v: "chat", l: "CHAT", hint: "Free-form chat with the bot." },
];

function copyToClip(text) {
  navigator.clipboard?.writeText(text)
    .then(() => toast.success("COPIED"))
    .catch(() => toast.error("COPY_FAILED"));
}

function ReferencePill({ ref, onRemove, idx }) {
  return (
    <span
      data-testid={`ref-pill-${idx}`}
      className="inline-flex items-center gap-1 max-w-[260px] border border-amber-400/50 bg-amber-400/10 text-amber-200 text-[10px] font-mono uppercase tracking-wider px-2 py-1"
    >
      <span className="truncate">
        {ref.kind === "idea"
          ? "IDEA"
          : ref.kind === "innovation"
          ? "INNOV"
          : ref.kind === "save"
          ? "LIB"
          : ref.kind === "user_lib"
          ? "NOTE"
          : "PASTE"}{" "}
        · {ref.label || ref.text?.slice(0, 30)}
      </span>
      <button onClick={onRemove} className="hover:text-white">
        <X size={10} />
      </button>
    </span>
  );
}

function Section({ section, sIdx }) {
  const { title, kind, items = [] } = section;
  return (
    <div
      data-testid={`bot-section-${sIdx}`}
      className="border border-[#27272A] bg-[#0E0E0E]"
    >
      <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
        <span className="text-[11px] font-mono uppercase tracking-wider text-white">
          {title}
        </span>
        <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
          {kind}
        </span>
      </div>
      <div className="p-3 space-y-2 text-[12px]">
        {kind === "compare" &&
          items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <div className="col-span-2 font-mono text-zinc-400 text-[11px] uppercase">
                {it.label}
              </div>
              <div className="col-span-5 text-zinc-200">{it.left}</div>
              <div className="col-span-5 text-zinc-200 border-l border-[#27272A] pl-2">
                {it.right}
              </div>
            </div>
          ))}
        {kind === "score" &&
          items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 items-center gap-2">
              <div className="col-span-1 text-amber-300 font-mono tabnum">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="col-span-6 text-zinc-100">{it.label}</div>
              <div className="col-span-2 tabnum text-emerald-400 font-mono">
                {it.score}/100
              </div>
              <div className="col-span-3 text-[11px] text-zinc-400">{it.reason}</div>
            </div>
          ))}
        {(kind === "list" || kind === "expand") &&
          items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-amber-400 font-mono tabnum">·</span>
              <div>
                {typeof it === "string" ? (
                  <span className="text-zinc-200">{it}</span>
                ) : (
                  <>
                    <span className="text-white font-mono mr-2">{it.label}</span>
                    <span className="text-zinc-400">{it.detail}</span>
                  </>
                )}
              </div>
            </div>
          ))}
        {kind === "plaque" && (
          <pre
            data-testid={`bot-plaque-${sIdx}`}
            className="text-[10.5px] leading-snug font-mono text-emerald-300 bg-[#050505] border border-emerald-400/20 p-2 overflow-x-auto whitespace-pre"
          >
            {items
              .map((it) => (typeof it === "string" ? it : it.detail || ""))
              .join("\n")}
          </pre>
        )}
        {kind === "plain" &&
          items.map((it, i) => (
            <p key={i} className="text-zinc-200 leading-relaxed">
              {typeof it === "string" ? it : it.detail || ""}
            </p>
          ))}
      </div>
    </div>
  );
}

function groundedPreview(structured) {
  const secs = structured?.sections || [];
  const sec = secs.find((s) => (s.title || "").toLowerCase() === "grounded conclusion")
    || secs.find((s) => (s.title || "").toLowerCase() === "final summary");
  const it = sec?.items?.[0];
  const t = typeof it === "string" ? it : it?.detail || "";
  return (t || structured?.summary || "").slice(0, 400);
}

function StructuredAnswer({ payload }) {
  if (!payload) return null;
  const di = payload.doi_integrity;
  return (
    <div className="space-y-3">
      {di && (
        <div
          data-testid="doi-integrity-badge"
          className={
            "flex flex-wrap items-center gap-2 px-3 py-1.5 border text-[10px] font-mono uppercase tracking-wider " +
            (di.unconfirmed === 0
              ? "border-emerald-400/40 bg-emerald-500/[0.05] text-emerald-300"
              : "border-amber-400/40 bg-amber-500/[0.05] text-amber-300")
          }
        >
          <ShieldCheck size={12} />
          DOI_INTEGRITY · {di.checked} verified
          <span className="text-zinc-500">·</span> {di.corrected} corrected
          <span className="text-zinc-500">·</span> {di.duplicates} duplicate collisions
          <span className="text-zinc-500">·</span> {di.unconfirmed} unconfirmed
        </div>
      )}
      <div className="border border-purple-500/40 bg-purple-500/5 p-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300 font-mono">
          MODE · {payload.mode}
        </div>
        <div className="mt-1 text-white text-[13px] leading-relaxed">
          {payload.summary}
        </div>
      </div>
      {(payload.sections || []).map((s, i) => (
        <Section key={i} section={s} sIdx={i} />
      ))}
      {payload.copy_blocks?.length > 0 && (
        <div className="border border-[#27272A]">
          <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            COPY_BLOCKS
          </div>
          <div className="p-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            {payload.copy_blocks.map((b, i) => (
              <div
                key={i}
                className="border border-[#27272A] bg-[#0A0A0A] flex flex-col"
              >
                <div className="px-2 py-1 border-b border-[#27272A] flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-zinc-400">
                  <span>{b.label}</span>
                  <button
                    data-testid={`copy-block-${i}`}
                    onClick={() => copyToClip(b.content)}
                    className="hover:text-white inline-flex items-center gap-1"
                  >
                    <Copy size={10} /> COPY
                  </button>
                </div>
                <pre className="p-2 text-[11px] text-zinc-200 whitespace-pre-wrap overflow-x-auto max-h-72">
                  {b.content}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReferencePicker({ onPick }) {
  const [tab, setTab] = useState("ideas");
  const [q, setQ] = useState("");
  const [addTitle, setAddTitle] = useState("");
  const [addText, setAddText] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const qc = useQueryClient();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const pdfInputRef = useRef(null);

  const { data: topics = [] } = useQuery({ queryKey: ["topics"], queryFn: fetchTopics });
  const [topicFilter, setTopicFilter] = useState("");
  const { data: ideas = [] } = useQuery({
    queryKey: ["ref-ideas", topicFilter],
    queryFn: () =>
      fetchIdeas({
        topic_slug: topicFilter || undefined,
        sort: "idea_rank",
        limit: 50,
      }),
    enabled: tab === "ideas",
  });
  const { data: innov = [] } = useQuery({
    queryKey: ["ref-innov"],
    queryFn: () => fetchInnovationItems({ sort: "item_rank", limit: 60 }),
    enabled: tab === "innov",
  });
  const { data: lib = [] } = useQuery({
    queryKey: ["ref-userlib"],
    queryFn: () => fetchUserLib(),
    enabled: tab === "lib",
  });
  const { data: saves = [] } = useQuery({
    queryKey: ["ref-saves"],
    queryFn: () => api.get("/saves").then((r) => r.data),
    enabled: tab === "saves",
  });

  const filt = (arr, getText) =>
    !q.trim() ? arr : arr.filter((x) => getText(x).toLowerCase().includes(q.toLowerCase()));

  const saveAndAttach = async () => {
    if (!addTitle.trim() && !addText.trim()) return;
    setAddBusy(true);
    try {
      const { data } = await (await import("../lib/api")).api.post("/saves", {
        layer: "custom", title: addTitle, text: addText,
      });
      onPick({
        kind: "user_lib", ref_id: data.id,
        label: `[custom] ${addTitle || addText.slice(0, 30)}`,
        text: addText || addTitle,
      });
      toast.success("ATTACHED · saved to MY_LIB");
      setAddTitle(""); setAddText("");
    } catch (e) {
      toast.error("SAVE_FAILED");
    } finally {
      setAddBusy(false);
    }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("PDF_ONLY");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("MAX_25MB");
      return;
    }
    setPdfBusy(true);
    setPdfProgress(0);
    try {
      const uploadId =
        (crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const CHUNK = 4 * 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const buf = await file.slice(i * CHUNK, (i + 1) * CHUNK).arrayBuffer();
        const bytes = new Uint8Array(buf);
        let bin = "";
        for (let j = 0; j < bytes.length; j += 0x8000)
          bin += String.fromCharCode.apply(null, bytes.subarray(j, j + 0x8000));
        await api.post("/saves/pdf/chunk", {
          upload_id: uploadId, index: i, chunk_b64: btoa(bin),
        });
        setPdfProgress(Math.round(((i + 1) / total) * 90));
      }
      const { data } = await api.post("/saves/pdf/complete", {
        upload_id: uploadId, filename: file.name,
      });
      setPdfProgress(100);
      qc.invalidateQueries({ queryKey: ["ref-saves"] });
      onPick({
        kind: "save", ref_id: data.id,
        label: `[PDF] ${file.name}`,
        text: (data.text || "").slice(0, 200),
      });
      toast.success(
        `PDF_ATTACHED · ${data.payload?.pages ?? "?"} PAGES · SAVED_TO_LIBRARY`
      );
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("PDF_UPLOAD_FAILED", {
        description: typeof d === "string" ? d : e.message,
      });
    } finally {
      setPdfBusy(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]">
      <div className="border-b border-[#27272A] flex flex-wrap">
        {[
          { v: "ideas", l: "IDEAS" },
          { v: "innov", l: "INNOVATION" },
          { v: "saves", l: "LIBRARY" },
          { v: "lib", l: "MY_LIB" },
          { v: "pdf", l: "+ PDF" },
          { v: "add", l: "+ ADD" },
        ].map((t) => (
          <button
            key={t.v}
            data-testid={`refpicker-tab-${t.v}`}
            onClick={() => setTab(t.v)}
            className={
              "px-3 h-8 text-[10px] font-mono uppercase tracking-wider " +
              (tab === t.v
                ? "border-b-2 border-amber-400 text-white"
                : "text-zinc-500 hover:text-white")
            }
          >
            {t.l}
          </button>
        ))}
      </div>
      {tab !== "add" && tab !== "pdf" && (
        <div className="p-2 border-b border-[#27272A] flex items-center gap-2">
          <input
            data-testid="refpicker-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH"
            className="h-7 flex-1 bg-[#0E0E0E] border border-[#27272A] px-2 text-[11px] font-mono text-white outline-none focus:border-amber-400"
          />
          {tab === "ideas" && (
            <select
              data-testid="refpicker-topic-filter"
              value={topicFilter}
              onChange={(e) => setTopicFilter(e.target.value)}
              className="h-7 bg-[#0E0E0E] border border-[#27272A] px-2 text-[10px] font-mono text-white"
            >
              <option value="">ALL_TOPICS</option>
              {topics.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.slug}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      <div className="max-h-[260px] overflow-y-auto">
        {tab === "pdf" && (
          <div className="p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300">
              ATTACH_A_PDF · BOT_READS_IT
            </div>
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf,.pdf"
              data-testid="refpicker-pdf-input"
              onChange={(e) => uploadPdf(e.target.files?.[0])}
              disabled={pdfBusy}
              className="w-full text-[11px] font-mono text-zinc-300 file:mr-2 file:h-8 file:px-3 file:border file:border-amber-400 file:bg-amber-400 file:text-black file:font-mono file:uppercase file:text-[10px] file:tracking-wider file:cursor-pointer file:hover:bg-amber-300"
            />
            {pdfBusy && (
              <div className="space-y-1" data-testid="refpicker-pdf-progress">
                <div className="h-1.5 bg-[#141414] border border-[#27272A]">
                  <div
                    className="h-full bg-amber-400 transition-[width]"
                    style={{ width: `${pdfProgress}%` }}
                  />
                </div>
                <div className="text-[10px] font-mono text-zinc-500 uppercase">
                  {pdfProgress < 90 ? `UPLOADING · ${pdfProgress}%` : "EXTRACTING_TEXT…"}
                </div>
              </div>
            )}
            <p className="text-[10px] text-zinc-500 font-mono leading-relaxed">
              Max 25 MB · text is extracted and saved to your Personal
              Library (tag: PDF), then attached so the bot can read it.
              Re-attach it any time from the LIBRARY tab to keep expanding
              your work.
            </p>
          </div>
        )}
        {tab === "add" && (
          <div className="p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300">
              ADD_YOUR_OWN_IDEA
            </div>
            <input
              data-testid="add-idea-title"
              value={addTitle}
              onChange={(e) => setAddTitle(e.target.value)}
              placeholder="Idea title (optional)"
              className="w-full h-8 bg-[#0E0E0E] border border-[#27272A] focus:border-amber-400 outline-none px-2 text-[12px] font-mono text-white"
            />
            <textarea
              data-testid="add-idea-text"
              value={addText}
              onChange={(e) => setAddText(e.target.value)}
              placeholder="Paste or type your idea / thought…"
              className="w-full h-28 bg-[#0E0E0E] border border-[#27272A] focus:border-amber-400 outline-none p-2 text-[12px] font-mono text-white resize-y"
            />
            <button
              data-testid="add-idea-save"
              disabled={(!addTitle.trim() && !addText.trim()) || addBusy}
              onClick={saveAndAttach}
              className="w-full h-9 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
            >
              {addBusy ? "SAVING…" : "SAVE_TO_LIB · ATTACH"}
            </button>
            <p className="text-[10px] text-zinc-500 font-mono leading-relaxed">
              Saves the idea to your Personal Library (layer: CUSTOM) and
              attaches it as a reference on this message.
            </p>
          </div>
        )}
        {tab === "ideas" &&
          filt(ideas, (x) => x.title).map((i) => (
            <button
              key={i.id}
              data-testid={`refpicker-idea-${i.id}`}
              onClick={() =>
                onPick({
                  kind: "idea",
                  ref_id: i.id,
                  label: `[${i.topic_slug}] ${i.title}`,
                  text: i.summary,
                })
              }
              className="block w-full text-left px-2 py-1 hover:bg-[#141414] border-b border-[#0E0E0E]"
            >
              <div className="text-[11px] text-white truncate">{i.title}</div>
              <div className="text-[10px] font-mono text-zinc-500 uppercase">
                {i.topic_slug} · rank {i.idea_rank.toFixed(1)}
              </div>
            </button>
          ))}
        {tab === "innov" &&
          filt(innov, (x) => x.title).map((i) => (
            <button
              key={i.id}
              data-testid={`refpicker-innov-${i.id}`}
              onClick={() =>
                onPick({
                  kind: "innovation",
                  ref_id: String(i.number),
                  label: `#${String(i.number).padStart(3, "0")} ${i.title}`,
                  text: i.summary,
                })
              }
              className="block w-full text-left px-2 py-1 hover:bg-[#141414] border-b border-[#0E0E0E]"
            >
              <div className="text-[11px] text-white truncate">
                <span className="text-amber-300 font-mono mr-2">
                  #{String(i.number).padStart(3, "0")}
                </span>
                {i.title}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 uppercase">
                {i.category} · NOV {i.novelty}
              </div>
            </button>
          ))}
        {tab === "saves" &&
          filt(saves, (x) => (x.title || "") + " " + (x.text || "") + " " + (x.tags || []).join(" ")).map((i) => (
            <button
              key={i.id}
              data-testid={`refpicker-save-${i.id}`}
              onClick={() =>
                onPick({
                  kind: "save",
                  ref_id: i.id,
                  label: `[LIB] ${(i.title || i.text || "untitled").slice(0, 46)}`,
                  text: (i.text || "").slice(0, 200),
                })
              }
              className="block w-full text-left px-2 py-1 hover:bg-[#141414] border-b border-[#0E0E0E]"
            >
              <div className="text-[11px] text-white truncate">
                {(i.tags || []).includes("pdf") && (
                  <span className="text-rose-300 font-mono mr-1.5">PDF</span>
                )}
                {i.pin_locked && (
                  <span className="text-amber-300 font-mono mr-1.5">PIN</span>
                )}
                {i.title || i.text || "untitled"}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 uppercase">
                {i.layer} · {(i.tags || []).slice(0, 3).join(" · ") || "no-tags"} · {fmtRelative(i.created_at)}
              </div>
            </button>
          ))}
        {tab === "lib" &&
          filt(lib, (x) => x.text).map((i) => (
            <button
              key={i.id}
              data-testid={`refpicker-lib-${i.id}`}
              onClick={() =>
                onPick({
                  kind: "user_lib",
                  ref_id: i.id,
                  label: `[saved · ${i.topic_slug || "general"}]`,
                  text: i.text,
                })
              }
              className="block w-full text-left px-2 py-1 hover:bg-[#141414] border-b border-[#0E0E0E]"
            >
              <div className="text-[11px] text-white truncate">{i.text}</div>
              <div className="text-[10px] font-mono text-zinc-500 uppercase">
                {i.topic_slug || "general"} · {fmtRelative(i.created_at)}
              </div>
            </button>
          ))}
        {((tab === "ideas" && ideas.length === 0) ||
          (tab === "innov" && innov.length === 0) ||
          (tab === "saves" && saves.length === 0) ||
          (tab === "lib" && lib.length === 0)) && (
          <div className="grid-bg p-6 text-center text-[11px] font-mono text-zinc-500 uppercase tracking-wider">
            NO_RESULTS
          </div>
        )}
      </div>
    </div>
  );
}

function PromptLibrary({ activeId, onPick, onNew }) {
  const qc = useQueryClient();
  const { data: prompts = [] } = useQuery({
    queryKey: ["prompts"],
    queryFn: fetchPrompts,
  });
  const del = useMutation({
    mutationFn: deletePrompt,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prompts"] }),
  });

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]">
      <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-400 flex items-center gap-2">
          <Library size={11} className="text-amber-400" />
          PROMPT_TEMPLATES
        </span>
        <button
          data-testid="prompt-new-button"
          onClick={onNew}
          className="text-[10px] font-mono uppercase text-zinc-400 hover:text-white inline-flex items-center gap-1"
        >
          <PlusCircle size={10} /> NEW
        </button>
      </div>
      <ul className="max-h-72 overflow-y-auto">
        {prompts.map((p) => (
          <li
            key={p.id}
            data-testid={`prompt-row-${p.id}`}
            className={
              "px-2 py-2 border-b border-[#0E0E0E] hover:bg-[#141414] cursor-pointer " +
              (activeId === p.id ? "bg-[#141414]" : "")
            }
            onClick={() => onPick(p)}
          >
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-white truncate">{p.name}</span>
              {p.is_default && (
                <span className="text-[9px] font-mono text-amber-300 border border-amber-300/40 px-1">
                  DEFAULT
                </span>
              )}
              <span className="ml-auto text-[9px] font-mono uppercase text-zinc-500">
                {p.mode}
              </span>
              {!p.is_default && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    del.mutate(p.id);
                  }}
                  className="text-zinc-500 hover:text-rose-400"
                >
                  <Trash2 size={10} />
                </button>
              )}
            </div>
            <div className="text-[10px] text-zinc-500 truncate font-mono">
              {p.description}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NewPromptDialog({ open, onClose }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [mode, setMode] = useState("chat");
  const [body, setBody] = useState("");
  const create = useMutation({
    mutationFn: () => createPrompt({ name, description: desc, mode, body, tags: [] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompts"] });
      toast.success("PROMPT_SAVED");
      setName("");
      setDesc("");
      setBody("");
      onClose();
    },
    onError: (e) => toast.error("SAVE_FAILED", { description: e?.message || "" }),
  });
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-xl border border-[#27272A] bg-[#0A0A0A]">
        <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
          <span className="text-[11px] font-mono uppercase tracking-wider">
            NEW_PROMPT_TEMPLATE
          </span>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X size={14} />
          </button>
        </div>
        <div className="p-3 space-y-2 text-[12px]">
          <input
            data-testid="newprompt-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="NAME"
            className="w-full h-8 bg-[#0E0E0E] border border-[#27272A] px-2 font-mono text-white"
          />
          <input
            data-testid="newprompt-desc"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="DESCRIPTION"
            className="w-full h-8 bg-[#0E0E0E] border border-[#27272A] px-2 font-mono text-white"
          />
          <select
            data-testid="newprompt-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="w-full h-8 bg-[#0E0E0E] border border-[#27272A] px-2 font-mono text-white"
          >
            {MODES.map((m) => (
              <option key={m.v} value={m.v}>
                {m.l}
              </option>
            ))}
          </select>
          <textarea
            data-testid="newprompt-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Prompt body / template…"
            className="w-full h-40 bg-[#0E0E0E] border border-[#27272A] px-2 py-2 font-mono text-white resize-y"
          />
          <button
            data-testid="newprompt-save"
            onClick={() => create.mutate()}
            disabled={!name.trim() || !body.trim() || create.isPending}
            className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-white bg-white text-black hover:bg-amber-300 disabled:opacity-50"
          >
            {create.isPending ? "SAVING…" : "SAVE_PROMPT"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResultToolbar({ messages, onClearOutput }) {
  // Locate the most recent assistant message.
  const last = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return messages[i];
    }
    return null;
  }, [messages]);

  const plainText = useMemo(() => {
    if (!last) return "";
    const s = last.structured;
    if (s && typeof s === "object") {
      const parts = [];
      if (s.title) parts.push("# " + s.title);
      if (s.summary) parts.push("\n" + s.summary);
      const sections = s.sections || s.blocks || [];
      sections.forEach((sec) => {
        if (sec.heading || sec.title) parts.push("\n## " + (sec.heading || sec.title));
        if (sec.text || sec.content) parts.push((sec.text || sec.content));
        (sec.bullets || sec.points || []).forEach((b) => parts.push("- " + b));
      });
      return parts.filter(Boolean).join("\n");
    }
    return last.text || "";
  }, [last]);

  const copy = async () => {
    if (!plainText) return;
    try {
      await navigator.clipboard.writeText(plainText);
      toast.success("RESULT_COPIED");
    } catch {
      toast.error("Copy not supported in this browser");
    }
  };

  const download = () => {
    if (!plainText) return;
    const blob = new Blob([plainText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
    a.download = `mindstein-result-${ts}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("DOWNLOADED · .txt");
  };

  const emailIt = () => {
    if (!plainText) return;
    const subject = encodeURIComponent("MindStein result");
    const body = encodeURIComponent(plainText.slice(0, 2000));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.success("EMAIL_DRAFT_OPENED");
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfEmailOpen, setPdfEmailOpen] = useState(false);
  const [lastPdf, setLastPdf] = useState(null);

  const buildSections = () => {
    const s = last?.structured;
    const sections = [];
    if (s?.summary) sections.push({ heading: "Summary", body: s.summary, kind: "text" });
    if (s?.sections?.length) {
      for (const sec of s.sections) {
        sections.push({
          heading: sec.heading || sec.title || "Section",
          body: sec.text || sec.content || "",
          kind: "text",
        });
        if ((sec.bullets || sec.points || []).length) {
          sections.push({
            heading: "",
            kind: "list",
            items: (sec.bullets || sec.points || []).map((b) => ({ text: b })),
          });
        }
      }
    } else if (plainText) {
      sections.push({ heading: "Result", body: plainText, kind: "text" });
    }
    return sections;
  };

  const downloadPdf = async () => {
    if (!plainText) return;
    setPdfBusy(true);
    try {
      const title = last?.structured?.title || "MindStein result";
      const subtitle = last?.mode ? `Mode: ${last.mode}` : "";
      const r = await api.post("/pdf/render", {
        title, subtitle, sections: buildSections(),
      });
      setLastPdf(r.data);
      // trigger download
      const bin = atob(r.data.body_b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.data.filename || "mindstein-export.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF_DOWNLOADED", { description: "Also auto-saved to your library" });
    } catch (e) {
      toast.error("PDF_FAILED", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setPdfBusy(false);
    }
  };

  const openPdfEmail = async () => {
    if (!plainText) return;
    if (!lastPdf) {
      // render + persist first
      setPdfBusy(true);
      try {
        const title = last?.structured?.title || "MindStein result";
        const subtitle = last?.mode ? `Mode: ${last.mode}` : "";
        const r = await api.post("/pdf/render", {
          title, subtitle, sections: buildSections(),
        });
        setLastPdf(r.data);
      } catch (e) {
        toast.error("PDF_FAILED", { description: e?.response?.data?.detail || e?.message });
        setPdfBusy(false);
        return;
      }
      setPdfBusy(false);
    }
    setPdfEmailOpen(true);
  };

  const save = async () => {
    if (!last || !plainText) return;
    // Versioned save — each save is a NEW library entry (keeps history) so
    // work can be expanded session over session. Version = assistant turn #.
    const vNum = Math.max(
      1, messages.filter((m) => m.role === "assistant").length
    );
    try {
      await api.post("/saves", {
        layer: "bot",
        title: `${last?.structured?.title || "MindStein result"} · v${vNum}`,
        text: plainText.slice(0, 4000),
        tags: ["bot-progress"],
        payload: {
          mode: last.mode,
          structured: last.structured || null,
          message_id: last.id,
          session_id: last.session_id || null,
          version: vNum,
        },
      });
      toast.success(`SAVED_TO_LIBRARY · v${vNum}`, {
        description: "Re-attach it from the LIBRARY tab to keep expanding.",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const disabled = !last;

  return (
    <div
      data-testid="bot-result-toolbar"
      className="border border-[#27272A] bg-[#0E0E0E] px-2 py-1.5 mt-2 flex flex-wrap items-center gap-1"
    >
      <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-500 mr-2 inline-flex items-center gap-1">
        <Sparkles size={10} className="text-amber-400" /> RESULT_ACTIONS
      </span>
      <button
        data-testid="bot-result-copy"
        onClick={copy}
        disabled={disabled}
        className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-white hover:text-black disabled:opacity-40 inline-flex items-center gap-1"
      >
        <Copy size={10} /> COPY
      </button>
      <button
        data-testid="bot-result-save"
        onClick={save}
        disabled={disabled}
        className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-emerald-500/40 text-emerald-300 hover:bg-emerald-400 hover:text-black disabled:opacity-40 inline-flex items-center gap-1"
      >
        <BookmarkPlus size={10} /> SAVE_TO_LIBRARY
      </button>
      <button
        data-testid="bot-result-download"
        onClick={download}
        disabled={disabled}
        className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-amber-500/40 text-amber-300 hover:bg-amber-400 hover:text-black disabled:opacity-40 inline-flex items-center gap-1"
      >
        <Download size={10} /> DOWNLOAD_TXT
      </button>
      <button
        data-testid="bot-result-pdf"
        onClick={downloadPdf}
        disabled={disabled || pdfBusy}
        className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-emerald-500/40 text-emerald-300 hover:bg-emerald-400 hover:text-black disabled:opacity-40 inline-flex items-center gap-1"
      >
        <Download size={10} /> {pdfBusy ? "…" : "DOWNLOAD_PDF"}
      </button>
      <button
        data-testid="bot-result-pdf-email"
        onClick={openPdfEmail}
        disabled={disabled || pdfBusy}
        className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-sky-500/40 text-sky-300 hover:bg-sky-400 hover:text-black disabled:opacity-40 inline-flex items-center gap-1"
      >
        <Mail size={10} /> EMAIL_PDF
      </button>
      <button
        data-testid="bot-result-email"
        onClick={emailIt}
        disabled={disabled}
        className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-purple-500/40 text-purple-300 hover:bg-purple-400 hover:text-black disabled:opacity-40 inline-flex items-center gap-1"
      >
        <Mail size={10} /> EMAIL
      </button>
      <button
        data-testid="bot-result-clear-output"
        onClick={onClearOutput}
        className="ml-auto h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-rose-500/40 text-rose-300 hover:bg-rose-400 hover:text-black inline-flex items-center gap-1"
      >
        <Trash2 size={10} /> CLEAR_OUTPUT
      </button>
      {pdfEmailOpen && lastPdf && (
        <PdfEmailDialog
          pdf={lastPdf}
          onClose={() => setPdfEmailOpen(false)}
        />
      )}
    </div>
  );
}

function PdfEmailDialog({ pdf, onClose }) {
  const [recipients, setRecipients] = useState("");
  const [subject, setSubject] = useState("MindStein export · " + (pdf?.filename || ""));
  const [body, setBody] = useState("Attached: MindStein-generated PDF export.");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const list = recipients.split(",").map((s) => s.trim()).filter((s) => s.includes("@"));
    if (!list.length) {
      toast.error("NEED_RECIPIENT", { description: "Add at least one email" });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/pdf/email", {
        pdf_id: pdf.pdf_id, recipients: list, subject, body,
      });
      toast.success("EMAIL_QUEUED", { description: `${r.data.queued} recipient(s) · MOCKED delivery` });
      onClose?.();
    } catch (e) {
      toast.error("SEND_FAILED", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="pdf-email-dialog"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md border border-sky-400/60 bg-[#0A0A0A] p-5"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-sky-300">
            EMAIL_PDF · {pdf.filename} ({(pdf.size / 1024).toFixed(1)} KB)
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X size={14} />
          </button>
        </div>
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          RECIPIENTS (comma-separated)
        </label>
        <input
          data-testid="pdf-email-recipients"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder="you@example.com, teammate@lab.org"
          className="w-full mt-1 h-9 px-2 bg-[#0E0E0E] border border-[#27272A] text-[12px] font-mono text-white focus:border-sky-400 outline-none"
        />
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-2">
          SUBJECT
        </label>
        <input
          data-testid="pdf-email-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full mt-1 h-9 px-2 bg-[#0E0E0E] border border-[#27272A] text-[12px] font-mono text-white focus:border-sky-400 outline-none"
        />
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-2">
          MESSAGE
        </label>
        <textarea
          data-testid="pdf-email-body"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full mt-1 p-2 bg-[#0E0E0E] border border-[#27272A] text-[12px] font-mono text-white focus:border-sky-400 outline-none"
        />
        <button
          data-testid="pdf-email-send"
          onClick={send}
          disabled={busy}
          className="mt-3 w-full h-10 border border-sky-400 bg-sky-400 text-black hover:bg-sky-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
        >
          {busy ? "SENDING…" : "SEND_EMAIL"}
        </button>
        <p className="text-[10px] font-mono text-zinc-500 mt-2 leading-relaxed">
          MOCKED · outgoing email is logged, not delivered. Wire SendGrid/Resend
          to activate real delivery.
        </p>
      </div>
    </div>
  );
}

export default function BotPage() {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState(null);
  const [exampleSessionId, setExampleSessionId] = useState(null);
  const [dismissedExamples, setDismissedExamples] = useState(() =>
    loadDismissedExamples(),
  );
  const [latestExec, setLatestExec] = useState(null);
  const [activeCats, setActiveCats] = useState(null);
  const [mode, setMode] = useState("combine");
  const [message, setMessage] = useState("");
  const [refs, setRefs] = useState([]);
  const [paste, setPaste] = useState("");
  const [activePrompt, setActivePrompt] = useState(null);
  const [showNewPrompt, setShowNewPrompt] = useState(false);
  const [showPicker, setShowPicker] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const bottomRef = useRef(null);
  const sendStartRef = useRef(null);
  const recoverTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(recoverTimerRef.current), []);

  // pick up references handed off from topic page
  useEffect(() => {
    try {
      const pending = JSON.parse(
        sessionStorage.getItem("mindstein_pending_refs") || "[]",
      );
      if (pending.length) {
        setRefs(pending);
        sessionStorage.removeItem("mindstein_pending_refs");
        toast.success(`${pending.length} REFERENCE(S) ATTACHED`);
      }
      const pendingPrompt = sessionStorage.getItem("mindstein_pending_prompt");
      if (pendingPrompt) {
        setMessage(pendingPrompt);
        sessionStorage.removeItem("mindstein_pending_prompt");
      }
    } catch { /* ignore */ }
  }, []);

  const { data: sessions = [] } = useQuery({
    queryKey: ["sessions"],
    queryFn: fetchSessions,
    refetchInterval: 30000,
  });
  const { data: messages = [], isFetching: messagesLoading } = useQuery({
    queryKey: ["session-msgs", sessionId],
    queryFn: () => (sessionId ? fetchSessionMessages(sessionId) : []),
    enabled: !!sessionId && !exampleSessionId,
  });

  // Two pre-populated example synthesis outputs so every user sees the
  // product's output shape immediately — even before running the bot.
  // Each example lives client-side, is individually dismissible, and is
  // hidden the moment a real session is selected.
  const visibleExamples = useMemo(
    () => EXAMPLE_SESSIONS.filter((e) => !dismissedExamples.has(e.id)),
    [dismissedExamples],
  );
  const activeExample = useMemo(
    () => EXAMPLE_SESSIONS.find((e) => e.id === exampleSessionId) || null,
    [exampleSessionId],
  );
  const displayMessages = activeExample ? activeExample.messages : messages;

  const dismissExample = (id) => {
    setDismissedExamples((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistDismissedExamples(next);
      return next;
    });
    if (exampleSessionId === id) setExampleSessionId(null);
    toast.success("EXAMPLE_REMOVED", {
      description: "It won't come back on this device.",
    });
  };

  const send = useMutation({
    mutationFn: () => {
      sendStartRef.current = Date.now();
      const allRefs = [...refs];
      if (paste.trim()) {
        allRefs.push({
          kind: "paste",
          label: "PASTED_TEXT",
          text: paste.trim(),
        });
      }
      return sendChat({
        session_id: sessionId,
        mode,
        message: message.trim() || (paste.trim() ? "(see pasted text)" : ""),
        references: allRefs,
        prompt_template_id: activePrompt?.id || null,
        categories: activeCats,
      });
    },
    onSuccess: (data) => {
      setSessionId(data.session_id);
      setMessage("");
      setPaste("");
      setRefs([]);
      // Phase 39 — Executive Summary slideshow (reply → diagram → PDF)
      if (data?.exec_summary) {
        setLatestExec({
          exec: data.exec_summary,
          markdown: data?.assistant_message?.structured?.markdown || "",
        });
        toast.success("Executive Summary + diagram saved to Research Library");
      }
      // Phase 32 · OneStamp — surface the auto-stamp receipt inline.
      if (data?.onestamp?.stamp_id) {
        toast.success("OneStamp saved · prior-art proof", {
          description: `stamp ${data.onestamp.stamp_id.slice(0, 10)}…`,
          action: {
            label: "OPEN_LIBRARY",
            onClick: () => (window.location.href = "/onestamp"),
          },
        });
      }
      qc.invalidateQueries({ queryKey: ["session-msgs", data.session_id] });
      qc.invalidateQueries({ queryKey: ["sessions"] });
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
        50,
      );
    },
    onError: (e) => {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Phase 38 — controlled early access. Backend returns a structured
      // 403 for Verified Explorers who haven't been added to the Quick
      // Reply whitelist yet. Show a professional message instead of a raw
      // error toast.
      if (status === 403 && detail && detail.error === "explorer_access") {
        toast.info("EXPLORER_MODE", {
          description: EXPLORER_MSG,
          duration: 12000,
        });
        return;
      }
      if (!e?.response || status === 502 || status === 504 || status === 524) {
        toast.info("LONG_SYNTHESIS — connection dropped, recovering output…", {
          description: "Deep syntheses can take several minutes. Watching for your result.",
        });
        startRecovery();
      } else {
        toast.error("BOT_FAILED", { description: (typeof detail === "string" ? detail : detail?.message) || e?.message || "" });
      }
    },
  });

  // Long DAILY_SYNTH runs can outlive the proxy timeout — the backend keeps
  // working and stores the reply (+ exec PDF/diagram). Poll until it lands.
  const startRecovery = () => {
    setRecovering(true);
    const startedAt = (sendStartRef.current || Date.now()) - 120000;
    const deadline = Date.now() + 10 * 60 * 1000;
    const tick = async () => {
      try {
        const sess = await fetchSessions();
        const target = sessionId
          ? sess.find((s) => s.id === sessionId)
          : sess.find((s) => new Date(s.updated_at).getTime() >= startedAt);
        if (target) {
          const msgs = await fetchSessionMessages(target.id);
          const fresh = msgs.some(
            (m) => m.role === "assistant" && new Date(m.created_at).getTime() >= startedAt,
          );
          if (fresh) {
            setRecovering(false);
            setSessionId(target.id);
            setMessage("");
            setPaste("");
            setRefs([]);
            qc.invalidateQueries({ queryKey: ["session-msgs", target.id] });
            qc.invalidateQueries({ queryKey: ["sessions"] });
            toast.success("SYNTHESIS_COMPLETE — output recovered");
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 300);
            return;
          }
        }
      } catch { /* keep polling */ }
      if (Date.now() < deadline) {
        recoverTimerRef.current = setTimeout(tick, 8000);
      } else {
        setRecovering(false);
        toast.error("SYNTHESIS_TIMEOUT — open your latest session in a minute; the output may still land there.");
      }
    };
    recoverTimerRef.current = setTimeout(tick, 8000);
  };

  const onPickRef = (r) =>
    setRefs((prev) => (prev.find((p) => p.ref_id === r.ref_id && p.kind === r.kind)
      ? prev
      : [...prev, r]));

  const { data: earlyAccess } = useQuery({
    queryKey: ["early-access-me"],
    queryFn: async () => {
      const { data } = await api.get("/early-access/me");
      return data;
    },
    staleTime: 60 * 1000,
    retry: 0,
  });
  const quickReplyAllowed = earlyAccess?.quick_reply_allowed !== false;
  const canSend = !send.isPending && quickReplyAllowed && (message.trim() || paste.trim() || refs.length > 0);

  // Phase 39 — Explorer submissions never hit /api/bot/chat. Any input
  // (blank, "see examples", vague, or a real research question) surfaces
  // the mandated pre-launch message. No raw errors, no "invalid" text.
  const EXPLORER_MSG = "MindStein is preparing controlled early access for live analysis. Explore the sample Quick Reply, Cross-Fusion, and Deep Synthesis workflows below to see how MindStein turns complex questions into structured next steps. Join the whitelist to be considered for live testing.";
  const handleSend = () => {
    if (!quickReplyAllowed) {
      toast.info("EXPLORER_MODE", { description: EXPLORER_MSG, duration: 12000 });
      try {
        document
          .querySelector('[data-testid="workspace-preview"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch { /* no-op */ }
      return;
    }
    send.mutate();
  };

  return (
    <div data-testid="bot-page" className="p-4 space-y-3">
      <AIDisclaimer testid="bot-ai-disclaimer" />
      <RedDisclaimer testid="bot-red-disclaimer" showBotNote />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
      {/* LEFT — templates + sessions */}
      <aside className="lg:col-span-3 space-y-3">
        <PromptLibrary
          activeId={activePrompt?.id}
          onPick={(p) => {
            setActivePrompt(p);
            setMode(p.mode);
            toast.success(`TEMPLATE · ${p.name.toUpperCase()}`);
          }}
          onNew={() => setShowNewPrompt(true)}
        />

        <div className="border border-[#27272A] bg-[#0A0A0A]">
          <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-400">
              SESSIONS
            </span>
            <button
              data-testid="new-session-button"
              onClick={() => {
                setSessionId(null);
                setExampleSessionId(null);
                setActivePrompt(null);
                setRefs([]);
                setMessage("");
                setPaste("");
              }}
              className="text-[10px] font-mono uppercase text-zinc-400 hover:text-white inline-flex items-center gap-1"
            >
              <PlusCircle size={10} /> NEW
            </button>
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {visibleExamples.map((e) => (
              <li
                key={e.id}
                data-testid={`example-session-${e.id}`}
                onClick={() => {
                  setSessionId(null);
                  setExampleSessionId(e.id);
                }}
                className={
                  "group px-2 py-2 border-b border-[#0E0E0E] hover:bg-[#141414] cursor-pointer " +
                  (exampleSessionId === e.id ? "bg-[#141414]" : "")
                }
              >
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-white truncate flex-1">
                    {e.title}
                  </span>
                  <span
                    className="text-[9px] font-mono uppercase tracking-wider border border-amber-400/50 bg-amber-400/10 text-amber-300 px-1.5 py-0.5"
                    title="Example synthesis · pre-populated so you can see what MindStein produces"
                  >
                    EXAMPLE
                  </span>
                  <button
                    data-testid={`example-dismiss-${e.id}`}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      dismissExample(e.id);
                    }}
                    title="Remove this example"
                    className="text-zinc-500 hover:text-rose-300 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={11} />
                  </button>
                </div>
                <div className="text-[10px] font-mono text-zinc-500">
                  {e.message_count} msgs · {fmtRelative(e.updated_at)}
                </div>
              </li>
            ))}
            {sessions.map((s) => (
              <li
                key={s.id}
                data-testid={`session-${s.id}`}
                onClick={() => {
                  setExampleSessionId(null);
                  setSessionId(s.id);
                }}
                className={
                  "px-2 py-2 border-b border-[#0E0E0E] hover:bg-[#141414] cursor-pointer " +
                  (sessionId === s.id ? "bg-[#141414]" : "")
                }
              >
                <div className="text-[11px] text-white truncate">{s.title}</div>
                <div className="text-[10px] font-mono text-zinc-500">
                  {s.message_count} msgs · {fmtRelative(s.updated_at)}
                </div>
              </li>
            ))}
            {sessions.length === 0 && visibleExamples.length === 0 && (
              <li className="p-3 text-[11px] font-mono text-zinc-500 text-center">
                NO_SESSIONS
              </li>
            )}
          </ul>
        </div>
      </aside>

      {/* CENTER — chat */}
      <main className="lg:col-span-6 flex flex-col gap-3 min-h-[70vh]">
        <header className="border border-[#27272A] bg-[#0A0A0A] p-3">
          <div className="flex items-center gap-2">
            <Bot size={14} className="text-amber-400" />
            <h1 className="font-mono text-lg tracking-tight">
              MINDSTEIN_BOT
            </h1>
            <span className="ml-2 text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
              · structured suggestions ·
            </span>
            {activePrompt && (
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase border border-amber-400/40 bg-amber-400/10 text-amber-200 px-2 py-1">
                <Sparkles size={10} />
                TEMPLATE: {activePrompt.name}
                <button
                  onClick={() => setActivePrompt(null)}
                  className="ml-1 hover:text-white"
                >
                  <X size={10} />
                </button>
              </span>
            )}
          </div>
          <p className="text-zinc-500 text-[12px] mt-1">
            Pick a mode, attach references from your libraries, paste a thought,
            and Mindstein returns structured, copy-paste-ready output.
          </p>
        </header>

        {/* Phase 38/39 — Verified Explorer gate. Rendered inline (not a modal)
            so users still see the workspace preview below. */}
        {earlyAccess && !quickReplyAllowed && (
          <div
            data-testid="bot-explorer-gate"
            className="border border-emerald-400/40 bg-emerald-500/[0.06] p-4"
          >
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300">
              <Sparkles size={11} /> PRIVATE_PREVIEW · LIVE_ANALYSIS_COMING_SOON
            </div>
            <p className="mt-2 text-[13px] text-zinc-100 leading-relaxed">
              MindStein is preparing controlled early access for live analysis.
              Explore the sample Quick Reply, Cross-Fusion, and Deep Synthesis
              workflows below to see how MindStein turns complex questions into
              structured next steps. Join the whitelist to be considered for
              live testing.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href="mailto:support@the-mindsteinlab.com?subject=Quick%20Reply%20access%20request&body=Hi%20MindStein%20Lab%2C%20I%27d%20like%20to%20join%20the%20whitelist%20for%20live%20testing.%20Here%27s%20what%20I%27m%20working%20on%3A%20"
                data-testid="bot-explorer-request-access"
                className="inline-flex items-center gap-1 h-8 px-3 border border-emerald-400 bg-emerald-400 text-black text-[11px] font-mono uppercase tracking-wider hover:bg-emerald-300"
              >
                JOIN_THE_WHITELIST
              </a>
              <a
                href="/innovation"
                className="inline-flex items-center gap-1 h-8 px-3 border border-[#27272A] text-zinc-200 text-[11px] font-mono uppercase tracking-wider hover:bg-[#141414] hover:text-white"
              >
                OPEN_INTELLIGENCE_LAYER
              </a>
            </div>
          </div>
        )}

        {/* messages */}
        <div className="flex-1 space-y-3 overflow-y-auto pr-1">
          {!sessionId && !exampleSessionId && displayMessages.length === 0 && (
            <div className="border border-dashed border-[#27272A] grid-bg p-8 text-center">
              <Bot size={28} className="mx-auto text-amber-400" />
              <div className="mt-2 text-[12px] font-mono uppercase tracking-wider text-zinc-400">
                NEW_SESSION · pick a template or attach references to begin
              </div>
              {visibleExamples.length > 0 && (
                <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-amber-300">
                  · or open an EXAMPLE from the SESSIONS panel to see a full synthesis ·
                </div>
              )}
            </div>
          )}
          {activeExample && (
            <div
              data-testid="example-session-banner"
              className="border border-amber-400/40 bg-amber-500/[0.05] px-3 py-2 flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-amber-300"
            >
              <Sparkles size={11} />
              EXAMPLE · READ_ONLY · pre-populated so you can see what MindStein produces
              <button
                data-testid={`example-dismiss-inline-${activeExample.id}`}
                onClick={() => dismissExample(activeExample.id)}
                className="ml-auto inline-flex items-center gap-1 border border-amber-400/40 px-2 py-0.5 hover:bg-amber-400/10"
              >
                <Trash2 size={10} /> REMOVE_EXAMPLE
              </button>
            </div>
          )}
          {displayMessages.map((m, mi) => (
            <div
              key={m.id}
              data-testid={`msg-${m.role}-${m.id}`}
              className={
                "border " +
                (m.role === "user"
                  ? "border-[#27272A] bg-[#0E0E0E]"
                  : "border-purple-500/30 bg-purple-500/[0.03]")
              }
            >
              <div className="px-3 py-1 border-b border-[#27272A] flex items-center text-[10px] font-mono uppercase tracking-wider">
                <span
                  className={m.role === "user" ? "text-amber-300" : "text-purple-300"}
                >
                  {m.role}
                </span>
                <span className="ml-2 text-zinc-500">· {m.mode}</span>
                {activeExample && (
                  <span className="ml-2 border border-amber-400/40 bg-amber-400/10 text-amber-300 px-1.5 py-0.5 text-[9px] tracking-wider">
                    EXAMPLE
                  </span>
                )}
                <span className="ml-auto text-zinc-600">
                  {fmtRelative(m.created_at)}
                </span>
              </div>
              <div className="p-3">
                {m.role === "user" ? (
                  <>
                    <div className="text-zinc-100 whitespace-pre-wrap text-[13px]">
                      {m.text}
                    </div>
                    {(m.references || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {m.references.map((r, idx) => (
                          <span
                            key={idx}
                            className="text-[10px] font-mono uppercase border border-[#27272A] text-zinc-400 px-2 py-1"
                          >
                            {r.kind} · {r.label || r.text?.slice(0, 30)}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <StructuredAnswer payload={m.structured} />
                    {!activeExample && m.role === "assistant" && (m.exec_summary || (latestExec && displayMessages[displayMessages.length - 1]?.id === m.id && latestExec.exec)) && (
                      <div className="mt-3">
                        <SummaryDiagramResults
                          exec={m.exec_summary || latestExec?.exec}
                          topic={
                            [...displayMessages.slice(0, mi)].reverse().find((x) => x.role === "user")?.text
                            || m.structured?.title || m.text || ""
                          }
                          messageId={m.id}
                          sessionId={m.session_id || sessionId || ""}
                          preview={groundedPreview(m.structured)}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          {(send.isPending || recovering) && (
            <div data-testid="bot-thinking" className="border border-purple-500/30 bg-purple-500/[0.03] p-3 text-[12px] font-mono text-purple-300 animate-pulse">
              {recovering
                ? "MINDSTEIN_IS_STILL_WORKING… deep syntheses can take several minutes — your output (plus its Summary and Diagram Results) will appear here automatically."
                : "MINDSTEIN_IS_THINKING…"}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* RESULT TOOLBAR — copy / save / download / email / clear */}
        {!activeExample && (
          <ResultToolbar
            messages={messages}
            onClearOutput={() => {
              // start a fresh session by dropping the cursor — local-only clear
              setSessionId(null);
              qc.invalidateQueries({ queryKey: ["session-msgs", null] });
              toast.success("OUTPUT_CLEARED — fresh session ready");
            }}
          />
        )}

        {/* composer */}
        <div className="border border-[#27272A] bg-[#0A0A0A]">
          <div className="border-b border-[#27272A] px-2 py-1.5">
            <CategoryFilter
              active={{ off: [] }}
              onChange={setActiveCats}
              autoPromptFirstLogin
            />
          </div>
          <div className="border-b border-[#27272A] p-2 flex flex-wrap items-center gap-1">
            {MODES.map((m) => (
              <button
                key={m.v}
                data-testid={`mode-${m.v}`}
                title={m.hint}
                onClick={() => setMode(m.v)}
                className={
                  "h-7 px-2 text-[10px] font-mono uppercase tracking-wider border " +
                  (mode === m.v
                    ? "border-amber-400 bg-amber-400 text-black"
                    : "border-[#27272A] text-zinc-400 hover:text-white hover:bg-[#141414]")
                }
              >
                {m.l}
              </button>
            ))}
          </div>
          {refs.length > 0 && (
            <div className="border-b border-[#27272A] p-2 flex flex-wrap gap-1">
              {refs.map((r, i) => (
                <ReferencePill
                  key={i}
                  ref={r}
                  idx={i}
                  onRemove={() =>
                    setRefs((p) => p.filter((_, j) => j !== i))
                  }
                />
              ))}
            </div>
          )}
          <textarea
            data-testid="bot-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              MODES.find((m) => m.v === mode)?.hint || "Type your message…"
            }
            className="w-full h-24 bg-transparent p-3 text-[13px] text-white font-sans outline-none resize-none"
          />
          <details className="border-t border-[#27272A] px-2 py-1">
            <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:text-white inline-flex items-center gap-1">
              <ChevronRight size={10} className="hidden [details[open]>summary>&]:hidden" />
              <ChevronDown size={10} className="hidden [details[open]>summary>&]:inline" />
              PASTE_TEXT_AS_REFERENCE
            </summary>
            <textarea
              data-testid="bot-paste-box"
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              placeholder="Paste raw text, notes, an article excerpt…"
              className="mt-1 w-full h-20 bg-[#0E0E0E] border border-[#27272A] p-2 text-[12px] text-white font-mono outline-none resize-y"
            />
          </details>
          <div className="border-t border-[#27272A] p-2 flex items-center justify-between gap-2 flex-wrap">
            <button
              data-testid="toggle-refpicker"
              onClick={() => setShowPicker((v) => !v)}
              className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:text-white inline-flex items-center gap-1"
            >
              {showPicker ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
              REFERENCES_PANEL
            </button>
            <div className="flex items-center gap-1">
              <button
                data-testid="bot-clear-input"
                onClick={() => {
                  setMessage("");
                  setPaste("");
                  toast.success("INPUT_CLEARED");
                }}
                disabled={!message && !paste}
                title="Clear text and paste box"
                className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
              >
                <Eraser size={10} /> CLEAR_INPUT
              </button>
              <button
                data-testid="bot-clear-selected"
                onClick={() => {
                  setRefs([]);
                  toast.success("SELECTED_REFS_CLEARED");
                }}
                disabled={refs.length === 0}
                title="Drop all attached references"
                className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
              >
                <X size={10} /> CLEAR_REFS
              </button>
              <button
                data-testid="bot-reset-prompt"
                onClick={() => {
                  setActivePrompt(null);
                  setMode("combine");
                  toast.success("PROMPT_RESET");
                }}
                disabled={!activePrompt}
                title="Reset to default mode + drop template"
                className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
              >
                <RotateCcw size={10} /> RESET_PROMPT
              </button>
              <button
                data-testid="bot-send-button"
                onClick={handleSend}
                disabled={send.isPending || (quickReplyAllowed && !(message.trim() || paste.trim() || refs.length > 0))}
                className="h-9 px-4 text-[11px] font-mono uppercase tracking-wider border border-white bg-white text-black hover:bg-amber-300 hover:border-amber-300 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Send size={11} />
                {send.isPending ? "SENDING…" : "SEND"}
              </button>
            </div>
          </div>
        </div>

        {/* Phase 39 — MindStein Workspace Preview. Placed directly below the
            bot area so Explorers can see the three sample workflows without
            leaving the composer. */}
        <WorkspacePreview />
      </main>

      {/* RIGHT — reference picker */}
      <aside className="lg:col-span-3 space-y-3">
        {showPicker && <ReferencePicker onPick={onPickRef} />}
        <div className="border border-[#27272A] bg-[#0A0A0A] p-3 text-[11px] text-zinc-400 leading-relaxed">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300 mb-2">
            HOW_IT_WORKS · 5_STEPS
          </div>
          <ol className="space-y-2 text-[12px]">
            <li className="flex gap-2">
              <span className="text-amber-400 font-mono shrink-0">①</span>
              <span><span className="text-white">Enter ideas</span> — pick refs from your libraries on the right, or paste raw text below the composer. <ArrowDown size={11} className="inline text-zinc-600 ml-1" /></span>
            </li>
            <li className="flex gap-2">
              <span className="text-amber-400 font-mono shrink-0">②</span>
              <span><span className="text-white">Group ideas</span> — attach as many references as you want. Drop unwanted ones via the X. <ArrowDown size={11} className="inline text-zinc-600 ml-1" /></span>
            </li>
            <li className="flex gap-2">
              <span className="text-amber-400 font-mono shrink-0">③</span>
              <span><span className="text-white">Remix</span> — pick a MODE (combine · compare · contrast · remix) or load a saved template. <ArrowDown size={11} className="inline text-zinc-600 ml-1" /></span>
            </li>
            <li className="flex gap-2">
              <span className="text-amber-400 font-mono shrink-0">④</span>
              <span><span className="text-white">Run the bot</span> — hit SEND. Mindstein returns a structured, copy-ready answer. <ArrowDown size={11} className="inline text-zinc-600 ml-1" /></span>
            </li>
            <li className="flex gap-2">
              <span className="text-amber-400 font-mono shrink-0">⑤</span>
              <span><span className="text-white">Save / export</span> — use the result toolbar to COPY · SAVE · DOWNLOAD · EMAIL the output.</span>
            </li>
          </ol>
        </div>
      </aside>

      <NewPromptDialog open={showNewPrompt} onClose={() => setShowNewPrompt(false)} />
      </div>
    </div>
  );
}
